import * as THREE from 'three';

export interface LightningBoltParams {
  startPoint: THREE.Vector3;
  endPoint: THREE.Vector3;
  generations: number;
  branchProbability: number;
  intensity: number;
  thickness: number;
}

export interface GroundStrike {
  position: THREE.Vector3;
  intensity: number;
  timestamp: number;
}

export class LightningBolt {
  private geometry: THREE.BufferGeometry;
  private material: THREE.LineBasicMaterial;
  private mesh: THREE.LineSegments;
  private glowMaterial: THREE.LineBasicMaterial;
  private glowMesh: THREE.LineSegments;
  private fadeOutSpeed: number = 0.88;
  private maxOpacity: number = 1;
  private initialIntensity: number;
  private pulsePhase: number = 0;
  private segments: [THREE.Vector3, THREE.Vector3][] = [];
  private groundStrikes: GroundStrike[] = [];

  constructor(scene: THREE.Scene, params: LightningBoltParams) {
    this.initialIntensity = params.intensity;
    
    // Create main bolt geometry
    this.geometry = new THREE.BufferGeometry();
    this.material = new THREE.LineBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0,
      linewidth: params.thickness || 3
    });
    
    // Create glow effect geometry
    const glowGeometry = new THREE.BufferGeometry();
    this.glowMaterial = new THREE.LineBasicMaterial({
      color: 0x88aaff,
      transparent: true,
      opacity: 0,
      linewidth: (params.thickness || 3) * 2
    });
    
    this.mesh = new THREE.LineSegments(this.geometry, this.material);
    this.glowMesh = new THREE.LineSegments(glowGeometry, this.glowMaterial);
    
    scene.add(this.glowMesh); // Add glow first (behind main bolt)
    scene.add(this.mesh);
    
    this.generateBolt(params);
  }

  private generateBolt(params: LightningBoltParams): void {
    const points: THREE.Vector3[] = [];
    const glowPoints: THREE.Vector3[] = [];
    this.segments = [];
    
    // Generate main bolt path with more sophisticated algorithm
    const mainPath = this.generateMainPath(params.startPoint, params.endPoint, 12 + Math.floor(params.intensity / 20));
    
    // Add main path segments
    for (let i = 0; i < mainPath.length - 1; i++) {
      this.segments.push([mainPath[i], mainPath[i + 1]]);
    }
    
    // Generate multiple branch generations for more complex patterns
    this.generateBranches(mainPath, this.segments, params.generations, params.branchProbability, params.intensity, 1);
    
    // Detect ground strikes
    this.detectGroundStrikes(params.intensity);
    
    // Convert segments to points arrays
    this.segments.forEach(([start, end]) => {
      points.push(start, end);
      glowPoints.push(start, end);
    });
    
    // Create geometries
    this.createGeometry(this.geometry, points);
    this.createGeometry(this.glowMesh.geometry as THREE.BufferGeometry, glowPoints);
    
    // Set materials based on intensity
    this.updateMaterials(params.intensity);
  }

  private generateMainPath(start: THREE.Vector3, end: THREE.Vector3, segments: number): THREE.Vector3[] {
    const path: THREE.Vector3[] = [start.clone()];
    
    // Create more natural lightning path with stepped progression
    for (let i = 1; i < segments; i++) {
      const t = i / segments;
      const point = start.clone().lerp(end, t);
      
      // Enhanced displacement with realistic lightning behavior
      const displacement = 25 - (t * 18); // Stronger displacement at top
      const horizontalVariation = (Math.random() - 0.5) * displacement;
      const depthVariation = (Math.random() - 0.5) * displacement * 0.6;
      
      // Add jagged, stepped motion characteristic of real lightning
      if (Math.random() > 0.7) {
        const additionalVariation = horizontalVariation * 0.5;
        point.x += horizontalVariation + additionalVariation;
      } else {
        point.x += horizontalVariation;
      }
      
      point.z += depthVariation;
      
      // Add occasional sharp direction changes
      if (Math.random() > 0.85) {
        point.x += (Math.random() - 0.5) * 15;
      }
      
      path.push(point);
    }
    
    path.push(end.clone());
    return path;
  }

  private generateBranches(
    mainPath: THREE.Vector3[], 
    segments: [THREE.Vector3, THREE.Vector3][], 
    generations: number, 
    branchProbability: number,
    intensity: number,
    generationLevel: number
  ): void {
    if (generations <= 0) return;
    
    const branchPoints: THREE.Vector3[] = [];
    const branchProbabilityAdjusted = branchProbability * (intensity / 100);
    
    // Select branch points with bias toward upper portions
    for (let i = 1; i < mainPath.length - 1; i++) {
      const heightFactor = 1 - (i / mainPath.length); // Higher probability at top
      const adjustedProbability = branchProbabilityAdjusted * (0.5 + heightFactor * 0.5);
      
      if (Math.random() < adjustedProbability) {
        branchPoints.push(mainPath[i]);
      }
    }
    
    // Generate branches with varying characteristics
    branchPoints.forEach(branchStart => {
      const numBranches = Math.random() > 0.7 ? 2 : 1; // Sometimes multiple branches from one point
      
      for (let b = 0; b < numBranches; b++) {
        const branchLength = (40 + Math.random() * 60) * (intensity / 100);
        const branchSegments = Math.max(2, Math.floor(3 + Math.random() * 4));
        
        // Create more varied branch directions
        const angle = Math.random() * Math.PI * 2;
        const elevation = -Math.random() * 0.4 - 0.1; // Branches go down and out
        
        // Create direction vector using let instead of const to avoid assignment errors
        let branchDirection = new THREE.Vector3(
          Math.cos(angle),
          elevation,
          Math.sin(angle)
        );
        branchDirection = branchDirection.normalize();
        
        // Create branch end point using proper vector operations
        const branchEnd = new THREE.Vector3().copy(branchStart).add(
          new THREE.Vector3().copy(branchDirection).multiplyScalar(branchLength)
        );
        
        // Create branch path with more jagged characteristics
        const branchPath = this.generateBranchPath(branchStart, branchEnd, branchSegments);
        
        // Add branch segments
        for (let i = 0; i < branchPath.length - 1; i++) {
          segments.push([branchPath[i], branchPath[i + 1]]);
        }
        
        // Recursive branching with decreasing probability
        if (Math.random() < 0.4 && generationLevel < 3) {
          this.generateBranches(
            branchPath, 
            segments, 
            generations - 1, 
            branchProbability * 0.6,
            intensity * 0.7,
            generationLevel + 1
          );
        }
      }
    });
  }

  private generateBranchPath(start: THREE.Vector3, end: THREE.Vector3, segments: number): THREE.Vector3[] {
    const path: THREE.Vector3[] = [start.clone()];
    
    for (let i = 1; i < segments; i++) {
      const t = i / segments;
      const point = start.clone().lerp(end, t);
      
      // More erratic movement for branches
      const displacement = 8 + Math.random() * 12;
      point.x += (Math.random() - 0.5) * displacement;
      point.y += (Math.random() - 0.5) * displacement * 0.3;
      point.z += (Math.random() - 0.5) * displacement * 0.5;
      
      path.push(point);
    }
    
    path.push(end.clone());
    return path;
  }

  private detectGroundStrikes(intensity: number): void {
    // Check all segments for ground strikes (y < -200 is considered ground level)
    this.segments.forEach(([start, end]) => {
      // Check if this segment crosses the ground
      if (start.y > -200 && end.y <= -200) {
        // Calculate intersection point with ground
        const t = (-200 - start.y) / (end.y - start.y);
        const strikePoint = start.clone().lerp(end, t);
        
        this.groundStrikes.push({
          position: strikePoint,
          intensity: intensity,
          timestamp: Date.now()
        });
      }
    });
  }

  private createGeometry(geometry: THREE.BufferGeometry, points: THREE.Vector3[]): void {
    const positions = new Float32Array(points.length * 3);
    points.forEach((point, index) => {
      positions[index * 3] = point.x;
      positions[index * 3 + 1] = point.y;
      positions[index * 3 + 2] = point.z;
    });
    
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  }

  private updateMaterials(intensity: number): void {
    this.maxOpacity = Math.min(1, intensity / 60); // More visible at lower intensities
    
    // Color progression based on intensity
    if (intensity > 90) {
      this.material.color.setHex(0xffffff); // Pure white for extreme intensity
      this.glowMaterial.color.setHex(0x88aaff);
    } else if (intensity > 70) {
      this.material.color.setHex(0xeeeeff); // Blue-white
      this.glowMaterial.color.setHex(0x6688ff);
    } else if (intensity > 50) {
      this.material.color.setHex(0xaaccff); // Light blue
      this.glowMaterial.color.setHex(0x4466ff);
    } else {
      this.material.color.setHex(0x88aaff); // Blue
      this.glowMaterial.color.setHex(0x3355ff);
    }
    
    this.material.opacity = this.maxOpacity;
    this.glowMaterial.opacity = this.maxOpacity * 0.6;
  }

  public update(): boolean {
    if (this.material.opacity <= 0.01) {
      return false; // Signal for removal
    }
    
    // Enhanced pulsing effect
    this.pulsePhase += 0.3;
    const pulseIntensity = 0.8 + Math.sin(this.pulsePhase) * 0.2;
    
    // More dramatic fade out with pulsing
    this.material.opacity *= this.fadeOutSpeed;
    this.glowMaterial.opacity *= this.fadeOutSpeed;
    
    // Apply pulsing effect
    this.material.opacity *= pulseIntensity;
    this.glowMaterial.opacity *= pulseIntensity * 0.7;
    
    // Occasional flicker for realism
    if (Math.random() < 0.15) {
      this.material.opacity *= 0.3;
      this.glowMaterial.opacity *= 0.5;
    }
    
    // Subtle color shifting during fade
    if (this.material.opacity < 0.5) {
      const fadeProgress = this.material.opacity / 0.5;
      this.material.color.lerp(new THREE.Color(0x4466aa), 1 - fadeProgress);
    }
    
    return true;
  }

  public getGroundStrikes(): GroundStrike[] {
    return this.groundStrikes;
  }

  public dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
    this.glowMaterial.dispose();
    (this.glowMesh.geometry as THREE.BufferGeometry).dispose();
    
    if (this.mesh.parent) {
      this.mesh.parent.remove(this.mesh);
    }
    if (this.glowMesh.parent) {
      this.glowMesh.parent.remove(this.glowMesh);
    }
  }
}

export class GroundStrikeEffect {
  private scene: THREE.Scene;
  private particles: THREE.Points;
  private geometry: THREE.BufferGeometry;
  private material: THREE.PointsMaterial;
  private sparks: THREE.Points;
  private sparkGeometry: THREE.BufferGeometry;
  private sparkMaterial: THREE.PointsMaterial;
  private groundFlash: THREE.PointLight;
  private fadeOutSpeed: number = 0.94;
  private maxOpacity: number = 1;
  private sparkCount: number = 0;
  private debrisCount: number = 0;
  private initialIntensity: number;

  constructor(scene: THREE.Scene, strike: GroundStrike) {
    this.scene = scene;
    this.initialIntensity = strike.intensity;
    
    // Create debris particles
    this.createDebrisParticles(strike);
    
    // Create spark particles
    this.createSparkParticles(strike);
    
    // Create ground flash light
    this.createGroundFlash(strike);
    
    scene.add(this.particles);
    scene.add(this.sparks);
    scene.add(this.groundFlash);
  }

  private createDebrisParticles(strike: GroundStrike): void {
    this.debrisCount = Math.floor(80 + (strike.intensity / 100) * 120);
    const positions = new Float32Array(this.debrisCount * 3);
    const velocities = new Float32Array(this.debrisCount * 3);
    const sizes = new Float32Array(this.debrisCount);
    
    for (let i = 0; i < this.debrisCount; i++) {
      const i3 = i * 3;
      
      // Start at strike position with small random spread
      positions[i3] = strike.position.x + (Math.random() - 0.5) * 8;
      positions[i3 + 1] = strike.position.y + Math.random() * 5;
      positions[i3 + 2] = strike.position.z + (Math.random() - 0.5) * 8;
      
      // Random velocities for debris explosion
      const angle = Math.random() * Math.PI * 2;
      const speed = 2 + Math.random() * 4;
      const elevation = Math.random() * 0.8 + 0.2;
      
      velocities[i3] = Math.cos(angle) * speed;
      velocities[i3 + 1] = elevation * speed;
      velocities[i3 + 2] = Math.sin(angle) * speed;
      
      // Random sizes
      sizes[i] = 0.5 + Math.random() * 2;
    }
    
    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.geometry.setAttribute('velocity', new THREE.BufferAttribute(velocities, 3));
    this.geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
    
    this.material = new THREE.PointsMaterial({
      color: 0x664422,
      size: 1.5,
      transparent: true,
      opacity: 0.8,
      blending: THREE.AdditiveBlending
    });
    
    this.particles = new THREE.Points(this.geometry, this.material);
  }

  private createSparkParticles(strike: GroundStrike): void {
    this.sparkCount = Math.floor(150 + (strike.intensity / 100) * 200);
    const sparkPositions = new Float32Array(this.sparkCount * 3);
    const sparkVelocities = new Float32Array(this.sparkCount * 3);
    const sparkSizes = new Float32Array(this.sparkCount);
    
    for (let i = 0; i < this.sparkCount; i++) {
      const i3 = i * 3;
      
      // Start at strike position
      sparkPositions[i3] = strike.position.x + (Math.random() - 0.5) * 4;
      sparkPositions[i3 + 1] = strike.position.y + Math.random() * 3;
      sparkPositions[i3 + 2] = strike.position.z + (Math.random() - 0.5) * 4;
      
      // Fast, erratic spark velocities
      const angle = Math.random() * Math.PI * 2;
      const speed = 5 + Math.random() * 8;
      const elevation = Math.random() * 1.2 + 0.3;
      
      sparkVelocities[i3] = Math.cos(angle) * speed + (Math.random() - 0.5) * 2;
      sparkVelocities[i3 + 1] = elevation * speed;
      sparkVelocities[i3 + 2] = Math.sin(angle) * speed + (Math.random() - 0.5) * 2;
      
      // Small spark sizes
      sparkSizes[i] = 0.2 + Math.random() * 0.6;
    }
    
    this.sparkGeometry = new THREE.BufferGeometry();
    this.sparkGeometry.setAttribute('position', new THREE.BufferAttribute(sparkPositions, 3));
    this.sparkGeometry.setAttribute('velocity', new THREE.BufferAttribute(sparkVelocities, 3));
    this.sparkGeometry.setAttribute('size', new THREE.BufferAttribute(sparkSizes, 1));
    
    this.sparkMaterial = new THREE.PointsMaterial({
      color: 0xffaa44,
      size: 0.8,
      transparent: true,
      opacity: 1,
      blending: THREE.AdditiveBlending
    });
    
    this.sparks = new THREE.Points(this.sparkGeometry, this.sparkMaterial);
  }

  private createGroundFlash(strike: GroundStrike): void {
    this.groundFlash = new THREE.PointLight(
      0xffffff, 
      (strike.intensity / 100) * 200, 
      150, 
      1.5
    );
    this.groundFlash.position.copy(strike.position);
    this.groundFlash.position.y += 10;
  }

  public update(): boolean {
    if (this.material.opacity <= 0.01) {
      return false; // Signal for removal
    }
    
    // Update debris particles
    const positions = this.geometry.attributes.position.array as Float32Array;
    const velocities = this.geometry.attributes.velocity.array as Float32Array;
    
    for (let i = 0; i < this.debrisCount; i++) {
      const i3 = i * 3;
      
      // Update positions
      positions[i3] += velocities[i3];
      positions[i3 + 1] += velocities[i3 + 1];
      positions[i3 + 2] += velocities[i3 + 2];
      
      // Apply gravity to debris
      velocities[i3 + 1] -= 0.12;
      
      // Apply air resistance
      velocities[i3] *= 0.98;
      velocities[i3 + 1] *= 0.98;
      velocities[i3 + 2] *= 0.98;
    }
    
    this.geometry.attributes.position.needsUpdate = true;
    
    // Update spark particles
    const sparkPositions = this.sparkGeometry.attributes.position.array as Float32Array;
    const sparkVelocities = this.sparkGeometry.attributes.velocity.array as Float32Array;
    
    for (let i = 0; i < this.sparkCount; i++) {
      const i3 = i * 3;
      
      // Update positions
      sparkPositions[i3] += sparkVelocities[i3];
      sparkPositions[i3 + 1] += sparkVelocities[i3 + 1];
      sparkPositions[i3 + 2] += sparkVelocities[i3 + 2];
      
      // Apply gravity to sparks
      sparkVelocities[i3 + 1] -= 0.15;
      
      // Apply heavy air resistance to sparks
      sparkVelocities[i3] *= 0.95;
      sparkVelocities[i3 + 1] *= 0.96;
      sparkVelocities[i3 + 2] *= 0.95;
    }
    
    this.sparkGeometry.attributes.position.needsUpdate = true;
    
    // Fade out materials
    this.material.opacity *= this.fadeOutSpeed;
    this.sparkMaterial.opacity *= this.fadeOutSpeed * 0.92; // Sparks fade faster
    
    // Fade out ground flash
    this.groundFlash.intensity *= 0.88;
    
    // Color shifting during fade
    if (this.material.opacity < 0.6) {
      const fadeProgress = this.material.opacity / 0.6;
      this.material.color.lerp(new THREE.Color(0x221100), 1 - fadeProgress);
    }
    
    if (this.sparkMaterial.opacity < 0.7) {
      const fadeProgress = this.sparkMaterial.opacity / 0.7;
      this.sparkMaterial.color.lerp(new THREE.Color(0x442200), 1 - fadeProgress);
    }
    
    return true;
  }

  public dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
    this.sparkGeometry.dispose();
    this.sparkMaterial.dispose();
    
    if (this.particles.parent) {
      this.particles.parent.remove(this.particles);
    }
    if (this.sparks.parent) {
      this.sparks.parent.remove(this.sparks);
    }
    if (this.groundFlash.parent) {
      this.groundFlash.parent.remove(this.groundFlash);
    }
  }
}

export class LightningManager {
  private scene: THREE.Scene;
  private activeBolts: LightningBolt[] = [];
  private activeGroundStrikes: GroundStrikeEffect[] = [];
  private maxBolts: number = 20;
  private maxGroundStrikes: number = 10;
  private totalGroundStrikes: number = 0;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  public createLightningBolt(flashPosition: THREE.Vector3, intensity: number): void {
    // Create multiple bolts for dramatic effect
    const boltCount = intensity > 90 ? 4 : intensity > 70 ? 3 : intensity > 50 ? 2 : 1;
    
    for (let i = 0; i < boltCount; i++) {
      const startPoint = flashPosition.clone();
      
      // Spread bolts for more dramatic sky coverage
      const spreadRadius = 60 + (intensity / 100) * 40;
      startPoint.x += (Math.random() - 0.5) * spreadRadius;
      startPoint.z += (Math.random() - 0.5) * spreadRadius;
      startPoint.y += Math.random() * 30;
      
      const endPoint = new THREE.Vector3(
        startPoint.x + (Math.random() - 0.5) * 150,
        -250 - Math.random() * 100,
        startPoint.z + (Math.random() - 0.5) * 150
      );
      
      const params: LightningBoltParams = {
        startPoint,
        endPoint,
        generations: Math.floor(2 + (intensity / 100) * 2),
        branchProbability: 0.4 + (intensity / 100) * 0.4,
        intensity: intensity + Math.random() * 30 - 15,
        thickness: 2 + (intensity / 100) * 2
      };
      
      const bolt = new LightningBolt(this.scene, params);
      this.activeBolts.push(bolt);
      
      // Check for ground strikes and create effects
      const groundStrikes = bolt.getGroundStrikes();
      groundStrikes.forEach(strike => {
        const groundStrikeEffect = new GroundStrikeEffect(this.scene, strike);
        this.activeGroundStrikes.push(groundStrikeEffect);
        this.totalGroundStrikes++;
      });
    }
    
    // Manage active bolts count
    if (this.activeBolts.length > this.maxBolts) {
      const oldBolt = this.activeBolts.shift();
      oldBolt?.dispose();
    }
    
    // Manage active ground strikes count
    if (this.activeGroundStrikes.length > this.maxGroundStrikes) {
      const oldStrike = this.activeGroundStrikes.shift();
      oldStrike?.dispose();
    }
  }

  public update(): void {
    // Update all active bolts and remove faded ones
    this.activeBolts = this.activeBolts.filter(bolt => {
      const shouldKeep = bolt.update();
      if (!shouldKeep) {
        bolt.dispose();
      }
      return shouldKeep;
    });
    
    // Update all active ground strikes and remove faded ones
    this.activeGroundStrikes = this.activeGroundStrikes.filter(strike => {
      const shouldKeep = strike.update();
      if (!shouldKeep) {
        strike.dispose();
      }
      return shouldKeep;
    });
  }

  public getTotalGroundStrikes(): number {
    return this.totalGroundStrikes;
  }

  public dispose(): void {
    this.activeBolts.forEach(bolt => bolt.dispose());
    this.activeBolts = [];
    this.activeGroundStrikes.forEach(strike => strike.dispose());
    this.activeGroundStrikes = [];
  }
}